import * as React from "react"

import { cn } from "@/lib/utils"

/*
  ActionBar — big touch buttons for site use (52px tall, 20px icons, ≥ 44px targets with gloves on).
  The 4 item actions of "Scan & move" (18 #10: Issue to site · Transfer · Reserve · Adjust), "Mark as checked" +
  "Photo" in site mode (09 #8).

    <ActionBar columns={2} actions={[
      { key: 'issue', label: 'Issue to site', icon: Truck, variant: 'primary', onSelect: issue },
      { key: 'transfer', label: 'Transfer', icon: ArrowLeftRight, onSelect: transfer },
      { key: 'reserve', label: 'Reserve', icon: Lock, onSelect: reserve, disabled: !canReserve, hint: 'Nothing available' },
      { key: 'adjust', label: 'Adjust', icon: Scale, onSelect: adjust },
    ]} />
    <ActionBar sticky actions={[{ key: 'check', label: 'Mark as checked', icon: CheckCheck, variant: 'primary', onSelect }]} />

  Action: { key, label, icon, onSelect, variant 'primary' | 'outline' (default) | 'danger', disabled, hint (shown
  as the button's title and to screen readers when disabled), busy (shows "…" and blocks), testId, span (columns) }.
  Props: actions, columns (2), sticky (sticks to the bottom of the screen, above the phone tab bar), className,
  ariaLabel ('Actions'), testId ('action-bar').
*/
const VARIANTS = {
  primary: "bg-brand text-brand-fg shadow-[0_1px_2px_rgb(25_25_112/0.25)] hover:bg-brand-hover",
  outline: "border border-line bg-surface text-ink shadow-card hover:bg-surface-hover",
  danger: "border border-[color:var(--danger-border)] bg-surface text-status-red-ink shadow-card hover:bg-status-red-bg",
}

function ActionBar({ actions, columns = 2, sticky = false, className, ariaLabel = "Actions", testId = "action-bar" }) {
  const visible = (actions || []).filter((action) => action && !action.hidden)
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      className={cn(
        "grid gap-2.5",
        sticky && "sticky bottom-[var(--app-bottom-nav-height,0px)] z-20 -mx-4 border-t border-line bg-[color-mix(in_srgb,var(--surface)_94%,transparent)] px-4 pb-[calc(12px+env(safe-area-inset-bottom,0px))] pt-3 backdrop-blur",
        className
      )}
      style={{ gridTemplateColumns: `repeat(${Math.max(1, Math.min(columns, visible.length || 1))}, minmax(0, 1fr))` }}
    >
      {visible.map((action) => {
        const Icon = action.icon
        const disabled = action.disabled || action.busy
        return (
          <button
            key={action.key || action.label}
            type="button"
            onClick={disabled ? undefined : action.onSelect}
            disabled={disabled}
            title={action.hint || undefined}
            aria-busy={action.busy || undefined}
            data-testid={action.testId || `${testId}-${action.key || action.label}`}
            className={cn(
              "inline-flex h-[52px] min-w-0 items-center justify-center gap-2 rounded-[14px] px-3 text-[15px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50",
              VARIANTS[action.variant] || VARIANTS.outline
            )}
            style={action.span ? { gridColumn: `span ${action.span} / span ${action.span}` } : undefined}
          >
            {Icon ? <Icon className="h-5 w-5 shrink-0" aria-hidden="true" /> : null}
            <span className="truncate">{action.busy ? `${action.label}…` : action.label}</span>
            {action.disabled && action.hint ? <span className="sr-only"> ({action.hint})</span> : null}
          </button>
        )
      })}
    </div>
  )
}

export { ActionBar }
